/**
 * Domain hooks for list-related PowerSync queries.
 *
 * Each hook encapsulates:
 *   - The canonical `queryKey` from the centralized factory
 *   - The `queryFn` (Drizzle query builder)
 *   - The `createPowerSyncQuery` / `createPowerSyncSingle` call
 *
 * Consumers import one of these hooks instead of calling
 * `createPowerSyncQuery` directly, making it impossible to create
 * duplicate subscriptions or mismatched query keys.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@listto/powersync";

import {
  getListDetailQuery,
  getListGroupsQuery,
  getListsWithMetaQuery,
  getListTodoCountsQuery,
  type ListGroup,
  type ListWithMeta,
  queryKeys,
} from "../index";

import {
  createPowerSyncQuery,
  createPowerSyncSingle,
  DEFAULT_THROTTLE_MS,
} from "./create-power-sync-query";

// ─── Shared option types ──────────────────────────────────────────────────────

interface BaseListQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  /** Stale time in ms. Pass your app's OFFLINE_CACHE_STALE_TIME_MS. */
  staleTime?: number;
  /** Optional extra throttle in ms. Defaults to 300ms. */
  throttleTime?: number;
}

// ─── useListsWithMetaQuery ────────────────────────────────────────────────────

/**
 * All custom lists with theme + group metadata for the current user.
 *
 * Used by: sidebar (route.tsx) — single authoritative subscription.
 * All other components should read from the TanStack cache with `createQuery`.
 */
export function useListsWithMetaQuery(options: BaseListQueryOptions) {
  return createPowerSyncQuery<ListWithMeta>({
    queryKey: () => queryKeys.lists.customLists(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getListsWithMetaQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
    // Non-suspending: this is the sidebar's authoritative subscription, mounted
    // at the _app layout level. If it suspended, the ENTIRE app shell would be
    // withheld (blank) behind the root <Suspense> until PowerSync's local DB is
    // ready. Signal-based so the shell renders instantly and the sidebar shows
    // its own granular loading state. Matches useSmartViewCountsQuery.
    suspense: false,
  });
}

// ─── useListGroupsQuery ───────────────────────────────────────────────────────

/**
 * All list groups for the current user.
 *
 * Used by: sidebar (route.tsx) — single authoritative subscription.
 */
export function useListGroupsQuery(options: BaseListQueryOptions) {
  return createPowerSyncQuery<ListGroup>({
    queryKey: () => queryKeys.lists.groups(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getListGroupsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
    // Non-suspending — see useListsWithMetaQuery above. Mounted at the _app
    // layout level; suspending here would blank the whole shell until DB ready.
    suspense: false,
  });
}

// ─── useListDetailQuery ───────────────────────────────────────────────────────

interface UseListDetailQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  listId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
  placeholderData?: Parameters<typeof createPowerSyncSingle<ListWithMeta>>[0]["placeholderData"];
  /**
   * Underlying query primitive. The web app passes `createAccountPowerSyncSingle`
   * so the SHARED TanStack cache is partitioned by the active per-tab account
   * (cross-account leak guard). Defaults to the account-agnostic primitive for
   * non-multi-account consumers.
   */
  queryFactory?: typeof createPowerSyncSingle<ListWithMeta>;
}

/**
 * Single list detail (theme + group) for a given `listId`.
 */
export function useListDetailQuery(options: UseListDetailQueryOptions) {
  const factory = options.queryFactory ?? createPowerSyncSingle<ListWithMeta>;
  return factory({
    queryKey: () => queryKeys.lists.detail(options.listId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.listId();
      return db && id ? getListDetailQuery(db, id) : null;
    },
    enabled: () => Boolean(options.listId()) && Boolean(options.drizzle()),
    placeholderData: options.placeholderData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── useListTodoCountsQuery ───────────────────────────────────────────────────

export interface ListTodoCountRow {
  list_id: string | null;
  count: number;
}

/**
 * Pending todo count grouped by `list_id` for the current user.
 *
 * Used by: Dashboard card badges.
 */
export function useListTodoCountsQuery(options: BaseListQueryOptions) {
  return createPowerSyncQuery<ListTodoCountRow>({
    queryKey: () => queryKeys.lists.todoCounts(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getListTodoCountsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
    // Non-suspending: consumed by the Dashboard cards. Suspending here made the
    // whole dashboard content region flash a skeleton + remount on cold load
    // while this sub-100ms local GROUP BY resolved. Signal-based instead.
    suspense: false,
  });
}
